// ~/modules/Songs/utils/fakeUltrastar.ts
type FakeSeed = {
    id: string;
    title: string;
    artist: string;
    video: string; // youtubeId
    language?: string | string[];
    year?: string | number;
};

function fnv1a(str: string) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function xorshift32(x: number) {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return x >>> 0;
}

function makeRng(seedStr: string) {
    let s = fnv1a(seedStr) || 1;
    return () => {
        s = xorshift32(s);
        return (s & 0xfffffff) / 0xfffffff;
    };
}

function clamp(n: number, a: number, b: number) {
    return Math.max(a, Math.min(b, n));
}

function pick<T>(rng: () => number, arr: T[]) {
    return arr[Math.floor(rng() * arr.length) % arr.length];
}

function normalizeLanguage(lang?: string | string[]) {
    if (!lang) return 'en';
    if (Array.isArray(lang)) return (lang[0] ?? 'en').trim() || 'en';
    return lang.trim() || 'en';
}

/**
 * Genera un .txt UltraStar compatible con TU convertTxtToSong().
 * Añade intro/outro sin notas para que no puntúe desde el segundo 0.
 */
export function generateFakeUltrastarTxt(seed: FakeSeed, durationSec = 240) {
    const rng = makeRng(`${seed.video}|${seed.title}|${seed.artist}|${seed.id}`);

    // BPM plausible (importante: no puede ser 0)
    const bpm = Math.round(clamp(90 + rng() * 60, 90, 160)); // 90..160
    const gapMs = Math.round(rng() * 800); // 0..800ms

    // Intro/outro deterministas por canción (demo “realista”)
    const introSec = Math.round(8 + rng() * 10); // 8..18s
    const outroSec = Math.round(5 + rng() * 6);  // 5..11s

    // beats aproximados (tu engine: beatLen = 60/bpm/bar *1000 con bar=4)
    const totalBeats = Math.max(64, Math.floor((durationSec * bpm) / 60));
    const introBeats = Math.floor((introSec * bpm) / 60);
    const outroBeats = Math.floor((outroSec * bpm) / 60);

    const startBeat = clamp(introBeats, 0, totalBeats - 16);
    const endBeat = clamp(totalBeats - outroBeats, startBeat + 16, totalBeats);

    const syllables = ['la', 'na', 'doo', 'da', 'oh', 'ya', 'hey'];
    const scale = [0, 2, 4, 5, 7, 9, 11]; // mayor
    const baseMidi = Math.round(52 + rng() * 8); // zona voz
    let lastPitch = baseMidi;

    const lines: string[] = [];

    lines.push(`#TITLE:${seed.title ?? 'Demo Song'}`);
    lines.push(`#ARTIST:${seed.artist ?? 'Demo Artist'}`);
    lines.push(`#LANGUAGE:${normalizeLanguage(seed.language)}`);
    if (seed.year !== undefined) lines.push(`#YEAR:${seed.year}`);
    lines.push(`#VIDEO:v=${seed.video ?? ''}`);
    lines.push(`#GAP:${gapMs}`);
    lines.push(`#BPM:${bpm}`);
    lines.push(`#CREATOR:AUTOGENERATED`);
    lines.push(`#GENRE:Demo`);
    lines.push(`#COMMENT:AUTOGENERATED_CHART intro=${introSec}s outro=${outroSec}s`);
    lines.push('');

    // ✅ Intro sin notas (sección vacía: no metemos "- 0" con notas)
    // Arrancamos las secciones directamente en startBeat
    let beat = startBeat;

    while (beat < endBeat) {
        // Alternar “bloques” cantables y descansos para que parezca verso/estribillo
        const singBlock = Math.floor(18 + rng() * 14); // 18..32 beats
        const restBlock = Math.floor(6 + rng() * 10);  // 6..16 beats

        const singEnd = Math.min(endBeat, beat + singBlock);

        // Sección cantable
        const lineLen = Math.max(12, Math.floor(16 + rng() * 12)); // 16..28
        let lineStart = beat;

        while (lineStart < singEnd) {
            const thisLineEnd = Math.min(singEnd, lineStart + lineLen);

            lines.push(`- ${lineStart}`);

            let local = 0;
            while (lineStart + local < thisLineEnd) {
                const len = Math.floor(1 + rng() * 3); // 1..3 beats

                const delta = pick(rng, [-2, -1, 0, 0, 0, 1, 2]);
                const degree = pick(rng, scale);
                const target = baseMidi + degree + delta * 2;

                const nextPitch = clamp(
                    Math.round(lastPitch + (target - lastPitch) * (0.35 + rng() * 0.45)),
                    40,
                    78,
                );
                lastPitch = nextPitch;

                const lyric = pick(rng, syllables);

                const isStar = rng() < 0.08;
                const typeChar = isStar ? '*' : ':';

                // Ojo: evita pasarte del final de línea
                const usableLen = Math.min(len, thisLineEnd - (lineStart + local));
                if (usableLen <= 0) break;

                lines.push(`${typeChar} ${lineStart + local} ${usableLen} ${nextPitch} ${lyric}`);

                local += usableLen;

                if (rng() < 0.10) local += 1; // micro-pausa
            }

            // siguiente línea dentro del bloque cantable
            lineStart = thisLineEnd + Math.floor(rng() * 3);
        }

        // Descanso (sin notas): saltamos beats sin añadir secciones
        beat = singEnd + restBlock;
    }

    lines.push('E');

    return lines.join('\n');
}